import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { translations, LANGUAGES } from '../i18n/translations';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
  languages: LANGUAGES,
});

const STORAGE_KEY = 'bp_marketing_lang';

function resolve(dict, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved && translations[saved]) return saved;
    } catch (e) {
      /* ignore */
    }
    return 'en';
  });

  const setLang = useCallback((next) => {
    if (!translations[next]) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* ignore */
    }
  }, []);

  // t() looks up the active language, falls back to English, then to the key itself.
  const t = useCallback(
    (path, fallback) => {
      const active = resolve(translations[lang], path);
      if (active !== undefined) return active;
      const en = resolve(translations.en, path);
      if (en !== undefined) return en;
      return fallback !== undefined ? fallback : path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, languages: LANGUAGES }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageContext;
